/**
 * Sentry error-tracking for the React client.
 *
 * Call `initSentry({ dsn, environment, isProd })` in main.tsx before `createRoot()`.
 * No-ops gracefully when dsn is falsy.
 */

import * as Sentry from '@sentry/react';
import { APP_VERSION, APP_NAME } from './constants.js';

let initialized = false;

export interface ClientSentryConfig {
  dsn?: string;
  environment: string;
  isProd: boolean;
}

/**
 * Initialise Sentry for the browser.
 * Safe to call more than once (idempotent).
 */
export function initSentry(config: ClientSentryConfig): void {
  if (!config.dsn || initialized) return;

  Sentry.init({
    dsn: config.dsn,
    release: `${APP_NAME.toLowerCase()}@${APP_VERSION}`,
    environment: config.environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: config.isProd ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: config.isProd ? 1.0 : 0,
    // Don't send PII by default
    sendDefaultPii: false,
  });

  initialized = true;
}

/**
 * Capture an exception and send it to Sentry (if initialised).
 */
export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(err, context ? { extra: context } : undefined);
}

/**
 * Check if Sentry has been initialized.
 */
export function isSentryInitialized(): boolean {
  return initialized;
}

export { Sentry };
